import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { TextField, Typography } from "@mui/material";

const EntryDetails = ({
  formData,
  setFormData,
  errors,
  setEntryDetailsErrors,
}) => {
  // Helper function to count words
  const getWordCount = (text) => {
    if (!text || text.trim() === '') return 0;
    return text.trim().split(/\s+/).length;
  };

  // Helper function to show word count status
  const getWordCountText = (text, fieldName) => {
    const wordCount = getWordCount(text);
    let color = 'textSecondary';
    let status = '';

    if (fieldName === "synopsis") {
      if (wordCount > 100) {
        color = 'error';
        status = ` (Maximum 100 words exceeded)`;
      } else {
        color = wordCount > 0 ? 'success' : 'textSecondary';
        status = ` (${wordCount}/100 words)`;
      }
    } else if (fieldName === "creative_note") {
      if (wordCount > 250) {
        color = 'error';
        status = ` (Maximum 250 words exceeded)`;
      } else {
        color = wordCount > 0 ? 'success' : 'textSecondary';
        status = ` (${wordCount}/250 words)`;
      }
    } else if (fieldName === "ai_collaboration") {
      if (wordCount < 50) {
        color = 'error';
        status = ` (Minimum 50 words required - ${50 - wordCount} more needed)`;
      } else if (wordCount > 200) {
        color = 'error';
        status = ` (Maximum 200 words exceeded)`;
      } else {
        color = 'success';
        status = ` (${wordCount}/200 words)`;
      }
    }

    return { text: status, color };
  };

  // Handle change for all fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate word count for text areas and set errors
    let errorMessage = "";
    const wordCount = getWordCount(value);

    if (name === "synopsis") {
      if (wordCount > 100) {
        errorMessage = "Short Synopsis cannot exceed 100 words";
      }
    } else if (name === "creative_note") {
      if (wordCount > 250) {
        errorMessage = "Creative Note cannot exceed 250 words";
      }
    } else if (name === "ai_collaboration") {
      if (wordCount < 50) {
        errorMessage = "AI Collaboration Narrative requires at least 50 words";
      } else if (wordCount > 200) {
        errorMessage = "AI Collaboration Narrative cannot exceed 200 words";
      }
    }

    // Update errors state
    if (errorMessage) {
      setEntryDetailsErrors({
        ...errors,
        [name]: errorMessage,
      });
    } else {
      // Clear error for this field if no error
      if (errors && errors[name]) {
        const newErrors = { ...errors };
        delete newErrors[name];
        setEntryDetailsErrors(newErrors);
      }
    }
  };

  return (
    <>
      <div className="cmot-phone">
        <h5>STEP 1</h5>
        <p>
          Entry Detail <span className="text-danger">*</span>
        </p>
      </div>
      <form>
        <Grid container spacing={2} className="cmot-grid mt-3">
          <Grid item xs={12} sm={6} lg={4} className="p-1">
            <Box>
              <TextField
                variant="outlined"
                label="Film/Project Title"
                type="text"
                fullWidth
                name="film_title"
                value={formData.film_title || ""}
                onChange={handleChange}
                error={!!errors.film_title}
                helperText={errors.film_title}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} className="p-1">
            <Box>
              <TextField
                variant="outlined"
                label="Duration (2–10 min)"
                type="text"
                fullWidth
                name="duration"
                value={formData.duration || ""}
                onChange={handleChange}
                error={!!errors.duration}
                helperText={errors.duration}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} className="p-1">
            <Box>
              <TextField
                variant="outlined"
                label="Genre/Theme"
                type="text"
                fullWidth
                name="genre_theme"
                value={formData.genre_theme || ""}
                onChange={handleChange}
                error={!!errors.genre_theme}
                helperText={errors.genre_theme}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} lg={12} className="p-1">
            <Box>
              <TextField
                id="synopsis"
                label="Short Synopsis (max 100 words)"
                multiline
                fullWidth
                rows={3}
                name="synopsis"
                value={formData.synopsis || ""}
                onChange={handleChange}
                error={!!errors.synopsis}
                helperText={
                  errors?.synopsis ? (
                    errors.synopsis
                  ) : (
                    <Typography
                      variant="body2"
                      color={getWordCountText(formData.synopsis || '', 'synopsis').color}
                    >
                      {getWordCountText(formData.synopsis || '', 'synopsis').text}
                    </Typography>
                  )
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} className="p-1">
            <Box mt={1} mb={1} ml={1}>
              <Typography variant="h6" component="h2">
                Creative Note
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} className="p-1">
            <Box>
              <TextField
                id="creative-note"
                label="Describe your film's concept, intent, and core message (max 250 words)"
                multiline
                fullWidth
                rows={4}
                name="creative_note"
                value={formData.creative_note || ""}
                onChange={handleChange}
                error={!!errors.creative_note}
                helperText={
                  errors?.creative_note ? (
                    errors.creative_note
                  ) : (
                    <Typography
                      variant="body2"
                      color={getWordCountText(formData.creative_note || '', 'creative_note').color}
                    >
                      {getWordCountText(formData.creative_note || '', 'creative_note').text}
                    </Typography>
                  )
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} className="p-1">
            <Box mt={2} mb={1} ml={1}>
              <Typography variant="h6" component="h2">
                Human-AI Collaboration Narrative
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} className="p-1">
            <Box>
              <TextField
                id="ai-collaboration"
                label="Explain briefly (3-5 lines) how human creativity and AI collaborated in your film (50-200 words)"
                multiline
                fullWidth
                rows={4}
                name="ai_collaboration"
                value={formData.ai_collaboration || ""}
                onChange={handleChange}
                error={!!errors.ai_collaboration}
                helperText={
                  errors?.ai_collaboration ? (
                    errors.ai_collaboration
                  ) : (
                    <Typography
                      variant="body2"
                      color={getWordCountText(formData.ai_collaboration || '', 'ai_collaboration').color}
                    >
                      {getWordCountText(formData.ai_collaboration || '', 'ai_collaboration').text}
                    </Typography>
                  )
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} className="p-1">
            <Box mt={2} mb={1} ml={1}>
              <Typography variant="h6" component="h2">
                Film Submission Links
              </Typography>
              <Typography variant="body2" color="red">
                Please share Google Drive or cloud storage links for your film
                and related documents. If files are password-protected, include
                the password below.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} className="p-1">
            <Box>
              <TextField
                id="film-link"
                label="Film / Video File Link (MP4 or MOV, 1080p, 16:9)"
                fullWidth
                name="film_link"
                value={formData.film_link || ""}
                onChange={handleChange}
                error={!!errors.film_link}
                helperText={errors.film_link}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} className="p-1">
            <Box>
              <TextField
                id="film-link-password"
                label="Film File Password (if applicable)"
                fullWidth
                name="film_link_password"
                value={formData.film_link_password || ""}
                onChange={handleChange}
                error={!!errors.film_link_password}
                helperText={errors.film_link_password}
              />
            </Box>
          </Grid>

          {/* <Grid item xs={12} sm={6} className="p-1">
            <Box>
              <TextField
                id="creative-note-link"
                label="Creative Note Document Link (PDF or text file)"
                fullWidth
                name="creative_note_link"
                value={formData.creative_note_link || ""}
                onChange={handleChange}
                error={!!errors.creative_note_link}
                helperText={errors.creative_note_link}
              />
            </Box>
          </Grid> */}

          {/* <Grid item xs={12} sm={6} className="p-1">
            <Box>
              <TextField
                id="creative-note-password"
                label="Creative Note File Password (if applicable)"
                fullWidth
                name="creative_note_password"
                value={formData.creative_note_password || ""}
                onChange={handleChange}
                error={!!errors.creative_note_password}
                helperText={errors.creative_note_password}
              />
            </Box>
          </Grid> */}
        </Grid>
      </form>
    </>
  );
};

export default EntryDetails;